import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn.js';


export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE = {
  sm: 'max-w-[420px]',
  md: 'max-w-[640px]',
  lg: 'max-w-[900px]',
} as const;

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  actions,
  footer,
  children,
  size = 'lg',
  className,
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
      document.body.style.overflow = 'hidden';
    } else if (!open && dialog.open) {
      dialog.close();
      document.body.style.overflow = '';
    }
  }, [open]);

  useEffect(() => () => void (document.body.style.overflow = ''), []);

  const onCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      event.preventDefault();
      onClose();
    },
    [onClose],
  );

  if (typeof document === 'undefined') return null;

  return createPortal(
    <dialog
      ref={ref}
      onCancel={onCancel}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
      aria-modal="true"
      className={cn(
        'm-auto w-[calc(100vw-2rem)] rounded-[var(--radius)] border border-[var(--line)] p-0',
        'max-h-[calc(100vh-3rem)] bg-[var(--surface)] text-[var(--ink)]',
        'shadow-[0_24px_64px_rgba(0,0,0,0.28)]',
        'backdrop:bg-black/45',
        SIZE[size],
        className,
      )}
    >
      {}
      <div className="flex max-h-[calc(100vh-3rem)] flex-col">
        <div className="flex items-start gap-3 border-b border-[var(--line)] px-[var(--pad)] py-3">
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-[14px] leading-tight font-semibold text-[var(--ink)]">
              {title}
            </h2>
            {subtitle && <div className="pt-0.5 text-[11.5px] text-[var(--ink-3)]">{subtitle}</div>}
          </div>
          {actions}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-noprint="1"
            className={cn(
              'grid h-7 w-7 shrink-0 cursor-pointer place-items-center rounded-[var(--radius-sm)]',
              'border-0 bg-transparent text-[var(--ink-3)]',
              'hover:bg-[var(--surface-2)] hover:text-[var(--ink)]',
            )}
          >
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
              <path
                d="M1 1L10 10M10 1L1 10"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

        {footer && (
          <div className="border-t border-[var(--line)] px-[var(--pad)] py-2.5">{footer}</div>
        )}
      </div>
    </dialog>,
    document.body,
  );
}
